/**
 * W1-11b (app-shell-ui): application start-up (called once by src/main.tsx; ARCHITECTURE.md §3.2 "main.tsx:
 * settings load, services, registry, i18n, React root, error boundary").
 *
 *  1. Resolve the services (W1-16 services.ts). If something needed at start-up fails, a crash screen says what,
 *     with Reload, instead of a blank page.
 *  2. Settings: start loading the stored user settings through the persist client (defaults apply meanwhile).
 *  3. Keyboard: install the focus regions (chrome buttons keep focus where it is; one input-modality tracker) and the
 *     keyboard router; attach the registry to the services with the router's context (module when-atoms, `init`s).
 *  4. UI service pointer tracking (popover anchors) and hash navigation.
 *  5. Errors that escape event handlers and promises become an error toast (one per message burst) instead of
 *     silently breaking a feature; the React tree has its own boundaries.
 *  6. Render <App> in StrictMode.
 * Every step is idempotent with wiring W1-16 may already have done (attach only once, start nav once).
 */

import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { AppKeymap } from '../registry/keymapCore';
import type { LayoutLabels } from '../registry/layoutLabels';
import type { AppRegistry } from '../registry/registryCore';
import type { AppServices } from '../registry/types';
import type { AppSettings } from '../settings/settingsService';
import { isShellNavService } from '../navService';
import { isShellUiService } from '../uiService';
import { App } from './App';
import { errorReport, errorText } from './download';
import { CrashScreen, reportLabels } from './ErrorBoundary';
import type { FocusRegions } from './FocusRegions';
import type { KeyboardRouter, KeyboardRouterDeps } from './KeyboardRouter';
import { i18n as runtimeI18n } from '../../i18n/runtime';
import { m } from './shell.messages';

export interface BootDeps {
  readonly root: HTMLElement;
  /** The app services (a function, so a failing service is caught and shown). */
  readonly services: () => AppServices;
  readonly registry: AppRegistry;
  readonly keymap: AppKeymap;
  readonly settings: Pick<AppSettings, 'attach' | 'loadState'>;
  readonly labels: LayoutLabels;
  readonly focusRegions: FocusRegions;
  readonly installRouter: (deps: KeyboardRouterDeps) => KeyboardRouter;
  readonly window?: Window;
}

export interface BootedApp {
  readonly root: Root;
  dispose(): void;
}

/** Messages that are noise, not failures. */
const IGNORED_ERRORS = [/ResizeObserver loop/i];
const ERROR_TOAST_QUIET_MS = 3000;

function installGlobalErrorToasts(win: Window, services: AppServices): () => void {
  const last = new Map<string, number>();
  const show = (err: unknown): void => {
    const message = errorText(err, services.i18n.t(m.unknownError));
    if (IGNORED_ERRORS.some((re) => re.test(message))) return;
    const now = Date.now();
    const prev = last.get(message);
    last.set(message, now);
    if (prev !== undefined && now - prev < ERROR_TOAST_QUIET_MS) return;
    try {
      services.ui.toast({ key: m.toastError, params: { error: message } }, { kind: 'error' });
    } catch {
      // The UI is gone; the console has the error.
    }
  };
  const onError = (e: ErrorEvent): void => {
    show(e.error ?? e.message);
  };
  const onRejection = (e: PromiseRejectionEvent): void => {
    show(e.reason);
  };
  win.addEventListener('error', onError);
  win.addEventListener('unhandledrejection', onRejection);
  return () => {
    win.removeEventListener('error', onError);
    win.removeEventListener('unhandledrejection', onRejection);
  };
}

/** Reads the services the shell cannot start without (a throwing getter fails here). */
function required(...services: readonly unknown[]): void {
  if (services.includes(undefined)) throw new Error('a required service is missing');
}

export function bootstrap(deps: BootDeps): BootedApp {
  const win = deps.window ?? window;
  const root = createRoot(deps.root);
  const disposers: Array<() => void> = [];
  let services: AppServices;
  let persist: AppServices['persist'];
  try {
    services = deps.services();
    // Touch what the shell needs at once, so a missing service fails here (with a message), not mid-render.
    required(services.registry, services.ui, services.nav, services.settings, services.i18n, services.editor);
    persist = services.persist;
  } catch (err) {
    console.error('Phiedit could not start', err);
    root.render(
      <StrictMode>
        <CrashScreen
          services={null}
          error={err}
          report={errorReport(err, null, reportLabels(runtimeI18n))}
          title={runtimeI18n.t(m.bootFailedTitle)}
          body={runtimeI18n.t(m.bootFailedBody, { error: errorText(err, runtimeI18n.t(m.unknownError)) })}
        />
      </StrictMode>,
    );
    return {
      root,
      dispose: () => {
        root.unmount();
      },
    };
  }

  if (deps.settings.loadState === 'idle') {
    deps.settings.attach(persist).catch((err: unknown) => {
      console.error('settings: could not load the stored settings', err);
    });
  }
  disposers.push(deps.focusRegions.install());
  const router = deps.installRouter({
    registry: deps.registry,
    keymap: deps.keymap,
    services: () => services,
    labels: deps.labels,
    window: win,
    focus: deps.focusRegions,
  });
  disposers.push(() => {
    router.dispose();
  });
  if (deps.registry.services === null) {
    disposers.push(deps.registry.attach({ services, when: () => router.whenContext() }));
  }
  if (isShellUiService(services.ui)) disposers.push(services.ui.install(win));
  if (isShellNavService(services.nav)) disposers.push(services.nav.start());
  disposers.push(installGlobalErrorToasts(win, services));

  root.render(
    <StrictMode>
      <App services={services} router={router} focus={deps.focusRegions} />
    </StrictMode>,
  );
  return {
    root,
    dispose() {
      root.unmount();
      for (const d of disposers.reverse()) d();
    },
  };
}
